import SlimSelect from 'slim-select';
import {getHash, setHash} from '../Utility/HashUtility';
import {getSearchLabel, SlimSelectController} from '../Controllers/SlimSelectController';
import {LoadingAnimation} from '../Components/LoadingAnimation';
import {initTooltips} from '../Controllers/TooltipController';
import lzString from 'lz-string';
import {init} from '@sentry/browser';
import {initLoadingAnimation} from '../Utility/LoadingAnimationUtility';

export class Contactforms {
  private salutationSelectElements: NodeListOf<HTMLSelectElement> | null;
  private salutationSlimSelects: SlimSelect[] = [];
  private countrySelectElements: NodeListOf<HTMLSelectElement> | null;
  private countrySlimSelects: SlimSelect[] = [];
  private contactFormElements: NodeListOf<HTMLElement> | null;
  private inputElements: NodeListOf<HTMLInputElement | HTMLTextAreaElement> | null;
  private rmaProductElements: NodeListOf<HTMLElement> | null = null;
  private formPrefillMapping: any = [];
  private salutationName = 'tx_dffcontact_contactforms[formData][salutation]';
  private countryName = 'tx_dffcontact_contactforms[formData][country]';
  private captchaReloadElements: NodeListOf<HTMLElement> | null = null;
  private returnReasonElements: NodeListOf<HTMLInputElement>;
  private descriptionReworkElement: HTMLInputElement | null;
  private hideRepairElements: NodeListOf<HTMLElement>;
  private notRepairRequiredElements: NodeListOf<HTMLInputElement>;
  private leadSourceFormField: HTMLInputElement | null;

  constructor() {
    this.salutationSelectElements = document.querySelectorAll('.js-contactform-select-salutation');
    this.countrySelectElements = document.querySelectorAll('.js-contactform-select-country');
    this.contactFormElements = document.querySelectorAll('.js-contactform, .js-powermailForm');
    this.inputElements = document.querySelectorAll('.js-contactform-input');
    this.returnReasonElements = document.querySelectorAll('.js-returnReason');
    this.descriptionReworkElement = document.querySelector('.js-descriptionRework');
    this.hideRepairElements = document.querySelectorAll('.js-hideRepair');
    this.notRepairRequiredElements = document.querySelectorAll('.js-notRepairRequired');
    this.leadSourceFormField = document.querySelector('.js-inquiry-leadsource');

    if (this.contactFormElements) {
      this.captchaReloadElements = document.querySelectorAll('.js-captcha-reload');
    }

    this.checkLeadSourceParameter();
    this.initSlimSelects();
    this.initFormPrefill();
    this.initSubmitListener();
    this.initRmaCountryListener();
    this.initRmaListeners();
    this.initCaptchaReload();
    this.initRepairListener();
    this.removePlaceholderOnClick();

    const hash = getHash();
    if (!hash) {
      return;
    }

    const powermailField = document.getElementById('powermail_field__your_requirements') as HTMLInputElement || null;
    if (powermailField) {
      const yourRequirements = lzString.decompressFromEncodedURIComponent(hash);
      if (yourRequirements) {
        powermailField.value = yourRequirements;
      }
      return;
    }

    if (hash.indexOf('|') >= 0) {
      const hashs = hash.split('|');
      this.setSubject(decodeURIComponent(hashs[1]));

      if (hashs[0] === 'inquiry') {
        const formKeyElement: HTMLInputElement | null = document.querySelector('.js-formKey');
        if (formKeyElement) {
          formKeyElement.value = 'product';
        }
      }
    }
  }

  protected initRmaCountryListener() {
    const countryFormElement: HTMLFormElement | null = document.querySelector('.js-formRmaCountry');
    if (countryFormElement) {
      const countrySelectElement: HTMLSelectElement | null = countryFormElement.querySelector('.js-contactform-select-country');
      if (countrySelectElement) {
        countrySelectElement.addEventListener('change', () => {
          if (!countryFormElement) {
            return;
          }
          /* trigger loading animation */
          const loadingAnimation = new LoadingAnimation('.js-formRmaCountry .js-loadingAnimation-country');
          if (loadingAnimation) {
            loadingAnimation.showLoadingAnimation();
          }

          countryFormElement.submit();
        });
      }
    }
  }

  protected initRmaListeners() {
    this.rmaProductElements = document.querySelectorAll('.js-rmaProduct');
    if (!this.rmaProductElements) {
      return;
    }

    const noClientNumberCheckbox: HTMLInputElement | null = document.querySelector('.js-noClientNumber');
    if (noClientNumberCheckbox) {
      noClientNumberCheckbox.addEventListener('change', () => {
        const additionalData = document.querySelector('.js-noClientNumberForm');
        if (!additionalData) {
          return;
        }

        const requiredElements: NodeListOf<HTMLInputElement> = additionalData.querySelectorAll('[data-required = "1"]');
        const clientNumberElement: HTMLInputElement | null = document.querySelector('.js-clientNumber');
        const clientNumberLabelElement: HTMLLabelElement | null = document.querySelector('.js-clientNumberLabel');
        if (!clientNumberElement || !clientNumberLabelElement) {
          return;
        }
        if (noClientNumberCheckbox.checked) {
          // set required fields to required for validation
          requiredElements.forEach((element) => {
            element.required = true;
          });
          clientNumberElement.required = false;
          clientNumberElement.disabled = true;
          clientNumberElement.value = '';
          clientNumberLabelElement.classList.remove('required');
          additionalData.classList.remove('hide');
        } else {
          requiredElements.forEach((element) => {
            element.required = false;
          });
          clientNumberElement.required = true;
          clientNumberElement.disabled = false;
          clientNumberLabelElement.classList.add('required');
          additionalData.classList.add('hide');
        }
      });
    }

    const riskRadioButtons: NodeListOf<HTMLInputElement> = document.querySelectorAll('.js-risk');
    const riskTextElement: HTMLInputElement | null = document.querySelector('.js-riskText');

    if (riskRadioButtons && riskTextElement) {
      riskRadioButtons.forEach((radiobutton) => {
        radiobutton.addEventListener('change', () => {
          const riskTextInputElement: HTMLInputElement | null = riskTextElement.querySelector('.js-contactform-input');

          if (radiobutton.value === '1') {
            riskTextElement.classList.remove('hide');
            if (riskTextInputElement) {
              riskTextInputElement.required = true;
            }
          } else {
            riskTextElement.classList.add('hide');
            if (riskTextInputElement) {
              riskTextInputElement.required = false;
            }
          }

          initTooltips();
        });
      });
    }

    // EventListeners for add / remove buttons
    const addRowButtons: NodeListOf<HTMLElement> = document.querySelectorAll('.js-showRow');
    addRowButtons.forEach((addButton) => {
      const rowToShowIndex = addButton.dataset.index;
      addButton.addEventListener('click', () => {

        this.rmaProductElements?.forEach((productElement) => {
          if (productElement.dataset.index === rowToShowIndex) {
            const inputElements: NodeListOf<HTMLInputElement> = productElement.querySelectorAll('.js-contactform-input');
            productElement.classList.remove('hide');
            productElement.querySelector('.js-interactionButtons')?.classList.remove('hide');

            // add required to input elements
            inputElements.forEach((inputElement) => {
              if (inputElement.dataset.required) {
                inputElement.required = true;
              }
            });
          }
        });

        // hide buttons for current row
        addButton.parentElement?.classList.add('hide');
      });
    });

    const removeRowButtons: NodeListOf<HTMLElement> = document.querySelectorAll('.js-hideRow');
    removeRowButtons.forEach((hideButton) => {
      const rowToHideIndex = hideButton.dataset.index || '';
      hideButton.addEventListener('click', () => {

        this.rmaProductElements?.forEach((productElement) => {
          const productIndex = productElement.dataset.index || '';
          //show buttons for previous element
          if (+productIndex === +rowToHideIndex - 1) {
            productElement.querySelector('.js-interactionButtons')?.classList.remove('hide');
          }

          // hide row, reset input values and remove required status
          if (productElement.dataset.index === rowToHideIndex) {
            const inputElements: NodeListOf<HTMLInputElement> = productElement.querySelectorAll('.js-contactform-input');
            productElement.classList.add('hide');
            productElement.querySelector('.js-interactionButtons')?.classList.add('hide');
            inputElements.forEach((inputElement) => {
              inputElement.value = '';
              if (inputElement.dataset.required) {
                inputElement.required = false;
              }
            });
          }
        });

        // hide hideButtons for current row
        hideButton.parentElement?.classList.add('hide');
      });
    });
  }

  private initSlimSelects() {
    if (this.salutationSelectElements) {
      for (let i = 0; i < this.salutationSelectElements.length; i++) {
        let slimselect = new SlimSelect({
          select: this.salutationSelectElements[i],
          showSearch: false,
        });
        this.salutationSlimSelects.push(slimselect);

        this.salutationSelectElements[i].addEventListener('change', () => {
          if (this.salutationSelectElements && this.salutationSelectElements[i]) {
            this.formPrefillMapping[this.salutationName] = this.salutationSelectElements[i].value;
          }
        });
      }
    }

    if (this.countrySelectElements) {
      for (let i = 0; i < this.countrySelectElements.length; i++) {
        let slimselect = new SlimSelect({
          select: this.countrySelectElements[i],
          showSearch: true,
          searchPlaceholder: getSearchLabel(),
        });
        this.countrySlimSelects.push(slimselect);

        this.countrySelectElements[i].addEventListener('change', () => {
          if (this.countrySelectElements && this.countrySelectElements[i]) {
            this.formPrefillMapping[this.countryName] = this.countrySelectElements[i].value;
          }
        });
      }
    }

    // devicetype slimselect
    const deviceTypeSelect = document.querySelector('.js-contactform-select-devicetype');
    if (deviceTypeSelect) {
      new SlimSelect({
        select: deviceTypeSelect,
        showSearch: false,
      });
    }
  }

  private initFormPrefill() {
    if (!this.inputElements) {
      return;
    }
    for (let i = 0; i < this.inputElements.length; i++) {
      this.inputElements[i].addEventListener('change', () => {
        if (this.inputElements) {
          this.formPrefillMapping[this.inputElements[i]?.name] = this.inputElements[i].value;
        }
      });
    }
  }

  private initSubmitListener() {
    this.contactFormElements?.forEach((formElement) => {
        const form = formElement.querySelector('form');
        const submitButtonElement: HTMLButtonElement | null = formElement.querySelector('input[type="submit"]');
        const formWrapperElement = formElement.parentElement;

        if (!form || !submitButtonElement || !formWrapperElement) {
          return;
        }

        submitButtonElement?.addEventListener('click', () => {
          const requiredElems: NodeListOf<HTMLInputElement> = form.querySelectorAll('input[required], textarea[required], select.slimSelect--required');
          for (let i = 0; i < requiredElems.length; i++) {
            requiredElems[i].classList.add('showValidity');
          }
        });

        initLoadingAnimation(submitButtonElement, formWrapperElement, form.classList.length > 0 ? `.${form.classList[0]}` : 'form');
      },
    );
  }

  private setSubject(subject: string) {
    const subjectInputs: NodeListOf<HTMLInputElement> = document.querySelectorAll('.js-contactform-subject');
    for (let i = 0; i < subjectInputs.length; i++) {
      subjectInputs[i].value = subject;
    }
    if (subject) {
      if (this.leadSourceFormField) {
        this.leadSourceFormField.value = 'Website Online Catalogue';
      }
    }
  }

  private initCaptchaReload() {
    if (this.captchaReloadElements) {
      const that = this;
      for (let i = 0; i < this.captchaReloadElements.length; i++) {
        this.captchaReloadElements[i].addEventListener('click', function () {
          const form = this.closest('form');
          if (form) {
            that.getNewCaptcha(form);
          }
        });
      }
    }
  }

  private async getNewCaptcha(form: HTMLFormElement) {
    const url = window.location.origin + window.location.pathname;
    try {
      fetch(url, {
        method: 'POST',
      }).then((response) => response.text()).then((content) => {
        const responseHTML = document.createElement('html');
        responseHTML.innerHTML = content;
        const captchaContainer: HTMLElement | null = responseHTML.querySelector('.' + form.classList[0]);
        if (!captchaContainer) {
          return;
        }
        const newResult: HTMLInputElement | null = captchaContainer.querySelector('.js-captchaResult');
        const newCaptchaImage: HTMLImageElement | null = captchaContainer.querySelector('.js-captchaImageContactform');
        const oldCaptchaImage: HTMLImageElement | null = form.querySelector('.js-captchaImageContactform');
        const oldResult: HTMLInputElement | null = form.querySelector('.js-captchaResult');
        if (newResult && oldResult) {
          oldResult.value = newResult.value;
        }
        if (oldCaptchaImage && newCaptchaImage) {
          oldCaptchaImage.src = newCaptchaImage.src;
        }
      });
    } catch (e) {
      throw e;
    }
  }

  private initRepairListener() {
    if (!this.returnReasonElements) {
      return;
    }

    this.returnReasonElements.forEach((element) => {
      element.addEventListener('change', () => {
        if (!this.descriptionReworkElement || !this.hideRepairElements) {
          return;
        }
        /* show rework element if repair is selected, hide other */
        if (element.checked && element.value === '3') {
          this.descriptionReworkElement.parentElement?.classList.remove('hide');
          this.hideRepairElements.forEach((hideElement) => {
            hideElement.classList.add('hide');
          });
          this.descriptionReworkElement.required = true;
          this.notRepairRequiredElements.forEach((requiredElement) => {
            requiredElement.required = false;
          });
        } else {
          this.descriptionReworkElement.parentElement?.classList.add('hide');
          this.hideRepairElements.forEach((hideElement) => {
            hideElement.classList.remove('hide');
          });
          this.descriptionReworkElement.required = false;
          this.notRepairRequiredElements.forEach((requiredElement) => {
            requiredElement.required = true;
          });
        }
      });
    });
  }

  private removePlaceholderOnClick() {
    const textarea: HTMLTextAreaElement | null = document.querySelector('.js-placeholder');
    if (!textarea) {
      return;
    }
    const placeholderText: string = textarea.placeholder;

    textarea.addEventListener('focus', () => {
      textarea.placeholder = '';
    });

    textarea.addEventListener('blur', () => {
      if (!textarea.value.trim()) {
        textarea.placeholder = placeholderText;
      }
    });
  }

  private checkLeadSourceParameter() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('supplierPortal') === '1') {

      if (this.leadSourceFormField) {
        this.leadSourceFormField.value = 'Supplier Portal';
      }
    }
  }
}
